import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputText } from "primereact/inputtext";
import { Password } from 'primereact/password';
import { classNames } from 'primereact/utils';
import { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { MobXProviderContext } from "mobx-react";
import { useNavigate, useLocation } from "react-router-dom";
import { RouterAddress } from "../../../constants/RouterAddress";
import AuthLayout from "../components/AuthLayout";
import { redirectToHomeForAuthMode } from '../../../utils/stringUtils';
import { appFormSectionStyle } from '../../../components/general/uiSurfaceStyles';
import { useAuthExperience } from '../hooks/useAuthExperience';
import { getCognitoBrandClientMetadata } from '../config/brandOverride';

const SignIn = () => {
  const navigate = useNavigate();
  const { authStore } = useContext(MobXProviderContext);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isOtpForm, setIsOtpForm] = useState(false);
  const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>(null);
  const authExperience = useAuthExperience();

  const location = useLocation();
  const email = location.state?.email || location.state?.username || "";
  const password = location.state?.password || "";
  const requireOTPWorkflow = location.state?.requireOTPWorkflow === true;

  const defaultValues = {
    username: email,
    password: password,
    otp: '',
  }
  const { control, formState: { errors }, handleSubmit, reset } = useForm({ defaultValues });

  useEffect(() => {
    if (email && password) {
      handleLogin({ username: email, password: password }, requireOTPWorkflow);
    }
  }, [email, password, requireOTPWorkflow]);

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const handleLogin = async (data: any, requireOTPWorkflow?: boolean) => {
    setLoading(true);
    setErrorMessage("");

    const authenticationDetails = new AuthenticationDetails({
      Username: data.username,
      Password: data.password,
      ClientMetadata: getCognitoBrandClientMetadata(),
    });

    const user = new CognitoUser({
      Username: data.username,
      Pool: authStore.userPoolProps,
    });

    setCognitoUser(user);

    if (requireOTPWorkflow) {
      user.setAuthenticationFlowType("CUSTOM_AUTH");
    } else {
      user.setAuthenticationFlowType("USER_PASSWORD_AUTH");
    }

    user.authenticateUser(authenticationDetails, {
      onSuccess: (session: CognitoUserSession) => {
        const finishLogin = async () => {
          authStore.setUserProperties(session, user.getUsername());
          await authStore.loadUserContext(true).catch(() => null);
          navigate(redirectToHomeForAuthMode(authStore.getAuthMode(), authStore.getUserRoleType()));
          window.location.reload();
        };
        void finishLogin();
      },
      onFailure: (error) => {
        console.error(error);
        setErrorMessage(error.message);
        setLoading(false);
      },
      newPasswordRequired: () => {
        authStore.setPendingNewPasswordUser(user);
        navigate(RouterAddress.RESET_PASSWORD, { state: { email: data.username, isTemporaryPassword: true } });
        setLoading(false);
      },
      customChallenge: () => {
        reset();
        setIsOtpForm(true);
        setLoading(false);
      },
      mfaRequired: () => {
        reset();
        setIsOtpForm(true);
        setLoading(false);
      },
    });
  };

  const handleOtpVerification = async (data: any) => {
    if (!cognitoUser) {
      return;
    }
    setLoading(true);
    setErrorMessage("");
    cognitoUser.sendCustomChallengeAnswer(data.otp, {
      onSuccess: async (session: CognitoUserSession) => {
        authStore.setUserProperties(session, cognitoUser.getUsername());
        await authStore.loadUserContext(true).catch(() => null);
        navigate(redirectToHomeForAuthMode(authStore.getAuthMode(), authStore.getUserRoleType()));
        window.location.reload();
      },
      onFailure: (error) => {
        setErrorMessage("Invalid OTP. Please try again.");
        setLoading(false);
      },
    });
  };

  return (
    <>
      <AuthLayout title={isOtpForm ? "Verify OTP" : "Login"} subtitle={isOtpForm ? "Enter the code sent to your email to finish signing in." : authExperience.auth.signInSubtitle}>
        {isOtpForm ? (
          <form onSubmit={handleSubmit(handleOtpVerification)} className="justify-content-center align-items-center flex-column flex" style={appFormSectionStyle}>
            <div className="field">
              <span className="p-float-label">
                <Controller name="otp" control={control} rules={{ required: 'OTP is required.' }} render={({ field, fieldState }) => (
                  <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                )} />
              </span>
              <small className="p-error">{errorMessage}</small>
            </div>

            <Button type="submit" label="Verify OTP" severity="warning" rounded className="mt-2 p-button-outlined" loading={loading} />

            <div className="grid" style={{ minWidth: "70%", alignItems: "center", textAlign: "center", marginTop: "1em" }}>
              <p className="col">
                Back to - &nbsp;
                <a onClick={() => setIsOtpForm(false)}>
                  Login
                </a>
              </p>
            </div>

          </form>
        ) : (
          <form onSubmit={handleSubmit((data) => handleLogin(data, true))}
            className="justify-content-center align-items-center flex-column flex" style={appFormSectionStyle}>
            <div className="field">
              <span className="p-float-label">
                <Controller name="username" control={control} rules={{ required: 'Username is required.' }} render={({ field, fieldState }) => (
                  <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                )} />
                <label htmlFor="username" className={classNames({ 'p-error': errors.username })}>Username</label>
              </span>
              {getFormErrorMessage('username')}
            </div>
            <div className="field">
              <span className="p-float-label">
                <Controller name="password" control={control} rules={{ required: 'Password is required.' }} render={({ field, fieldState }) => (
                  <Password id={field.name} {...field} feedback={false} toggleMask className={classNames({ 'p-invalid': fieldState.invalid })} />
                )} />
                <label htmlFor="password" className={classNames({ 'p-error': errors.password })}>Password</label>
              </span>
              {getFormErrorMessage('password')}
            </div>

            <div className="p-error">{errorMessage}</div>

            <Button type="submit" label="Login" severity="warning" rounded className="mt-2 p-button-outlined" loading={loading} />

            <div className="grid" style={{ minWidth: "70%", alignItems: "center", textAlign: "center", marginTop: "1em" }}>
              <p className="col">
                <a onClick={() => navigate(RouterAddress.SIGN_UP)}>
                  Sign Up
                </a>
              </p>
              <Divider layout="vertical" />
              <p className="col">
                <a onClick={() => navigate(RouterAddress.FORGOT_PASSWORD)}>
                  Forgot Password
                </a>
              </p>
            </div>
          </form>
        )}
      </AuthLayout>
    </>
  );
};

export default SignIn;
